import { DateTime } from '@ksyos/date-time';
import { Box, Button, ButtonRow, Col, Heading, Row } from '@ksyos/design-system';
import { bind } from 'bind-decorator';
import { isEqual } from 'lodash';
import { connect } from 'react-redux';
import type { EpisodeCategory } from '@common/enums';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { Profession } from '@common/enums';
import { ActionsPermission } from '@common/enums/actionPermission';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { getOneActionPermission } from '@frontend/services/api/actionPermissionApiService';
import { FrontendSettingsContext } from '@frontend/utils/hooks/useFrontendSettings';
import type { OphthalmologyPractitionerRole } from '../../../../common/enums/ophthalmologyPractitionerRole';
import type { IRequest } from '../../../../common/interfaces/request';
import type { IReduxState } from '../../../configureStore';
import type { IPatientTask } from '../../interfaces/patientTask';
import type { IRequestsFindFilter } from '../../interfaces/requestsFindFilter';
import type { PatientTaskListHeader } from './requestsList/PatientTasksList';
import type { RequestListHeader, SortDirection } from './requestsList/RequestsList';
import type { ISearchFilter } from './requestsList/RequestsListHeader';
import { WorkListFilter } from '../../../../common/enums/workListFilter';
import { UrlBuilder } from '../../../../common/utils/urlBuilder';
import { AbortableFetch, fetchJson } from '../../../store/fetch';
import { serviceConsumersSelector } from '../../../store/serviceConsumers/serviceConsumerActions';
import { translateKey } from '../../../translate';
import { DateInput, Loader } from '../../elements/common';
import { Component } from '../../elements/Component';
import { FirstUseTour } from '../../elements/firstUseTour/FirstUseTour';
import { ListPager } from '../../elements/ListPager';
import { FullWidthTemplate } from '../../templates/FullWidthTemplate';
import { DatePeriodSelection } from '../processes/adminProcessesList/header/DatePeriodSelection';
import { TwoFactorAuthenticationAlert } from '../profile/TwoFactorAuthenticationAlert';
import { PatientTasksList } from './requestsList/PatientTasksList';
import { RequestListAccordion } from './requestsList/RequestListAccordion';
import { RequestsList } from './requestsList/RequestsList';
import { RequestsListHeader } from './requestsList/RequestsListHeader';
import { addSearchFilters } from './util/buildGroupedWorklistUrl';
import { buildWorklistUrl } from './util/buildWorklistUrl';
import { requestFindFilter } from './util/requestFindFilter';

type IRequestsListContainerProps = IWithRouterProps & ReturnType<typeof mapStateToProps>;

const emptySearchFilter = {
    endDate: null,
    practitionerId: null,
    serviceId: null,
    startDate: null,
    text: '',
    typeOfTasksSelected: [],
    usersAndProfessions: {},
};

interface IRequestsListContainerState {
    enable2FaAlert: boolean;
    exportAfterDate: string | null;
    exportBeforeDate: string | null;
    filterData?: IRequestsFindFilter;
    hasPatientTaskPermission: boolean;
    isBiometricianTcoh?: boolean;
    isFirstUseTourVisible?: boolean;
    isSearching: boolean;
    isServiceEmploymentRequester?: boolean;
    isServiceReader?: boolean;
    isTwoFactorAuthenticationEnabled: boolean;
    itemsPerPage: number;
    month: number | null;
    monthTotal?: number;
    ophthalmologyPractitionerRole: OphthalmologyPractitionerRole | null;
    page: number;
    patientTaskEpisodeCategories: EpisodeCategory[];
    patientTasks: IPatientTask[];
    patientTaskTypes: string[];
    requests: IRequest[];
    searchFilter: ISearchFilter;
    sortByPatientTaskList?: PatientTaskListHeader;
    sortByRequestList?: RequestListHeader;
    sortDirectionPatientTaskList?: SortDirection;
    sortDirectionRequestList?: SortDirection;
    total: number;
    workListFilter: WorkListFilter | undefined;
    year: number | null;
}

type SortState = Pick<
    IRequestsListContainerState,
    ('sortByPatientTaskList' | 'sortDirectionPatientTaskList') | ('sortByRequestList' | 'sortDirectionRequestList')
>;

class RequestsListContainerWrapped extends Component<IRequestsListContainerProps, IRequestsListContainerState> {
    static contextType = FrontendSettingsContext;
    declare context: React.ContextType<typeof FrontendSettingsContext>;

    private readonly defaultItemsPerPage: number;
    private readonly abortableFetch = new AbortableFetch();
    private readonly abortController = new AbortController();

    constructor(props: IRequestsListContainerProps) {
        super(props);

        this.defaultItemsPerPage = 20;
        this.state = {
            enable2FaAlert: this.context?.frontendSettings.enable2FaAlert ?? false,
            exportAfterDate: null,
            exportBeforeDate: null,
            hasPatientTaskPermission: false,
            isSearching: true,
            isTwoFactorAuthenticationEnabled: true,
            itemsPerPage: this.defaultItemsPerPage,
            month: null,
            ophthalmologyPractitionerRole: null,
            page: 1,
            patientTaskEpisodeCategories: [],
            patientTaskTypes: [],
            patientTasks: [],
            requests: [],
            searchFilter: emptySearchFilter,
            total: 0,
            workListFilter: this.defaultWorkListFilter(),
            year: null,
        };
    }

    public async componentDidMount() {
        await this.setState({
            sortByRequestList: 'sortDate',
            sortDirectionRequestList: 'DESC',
        });

        await this.setState({
            sortByPatientTaskList: 'expectedDateCompleted',
            sortDirectionPatientTaskList: 'ASC',
        });

        await this.loadTwoFactorAuthenticationSettings();
        await this.loadServiceReaders();
        await this.loadBiometriciansTcoh();
        await this.loadServiceEmploymentRequester();

        const historyState = history.state?.usr;
        if (historyState?.workListFilter) {
            await this.setState({ workListFilter: historyState?.workListFilter });
        }
        if (historyState?.searchFilter) {
            await this.setState({ searchFilter: historyState?.searchFilter });
        }
        await this.search();

        await this.loadOphthalmologyRole();

        await this.hasPatientTaskPermission();

        await this.setState({ isFirstUseTourVisible: true });
    }

    public async componentWillUnmount() {
        this.abortableFetch.abort();
        this.abortController.abort();
    }

    private async hasPatientTaskPermission() {
        const { canRead } = await getOneActionPermission({
            action: ActionsPermission.PatientTask,
            signal: this.abortController.signal,
        });
        this.setState({ hasPatientTaskPermission: canRead });
    }

    public render() {
        return (
            <FullWidthTemplate>
                {this.context?.frontendSettings.enable2FaAlert && !this.state.isTwoFactorAuthenticationEnabled && (
                    <TwoFactorAuthenticationAlert
                        hasIcon={true}
                        practitionerId={this.props.authentication.practitionerId}
                        style={{ marginBottom: -24 }}
                    />
                )}

                <RequestsListHeader
                    applyFilter={this.applyFilter}
                    fetchedPatientTasksEpisodeCategories={this.state.patientTaskEpisodeCategories}
                    fetchedPatientTasksTypes={this.state.patientTaskTypes}
                    filterWorkList={this.filterWorkList}
                    onSearchFilterChange={this.handleSearch}
                    searchFilter={this.state.searchFilter}
                    showOngoingRequests={
                        this.props.isServiceConsumer ||
                        this.state.isServiceReader ||
                        this.state.isBiometricianTcoh ||
                        this.state.isServiceEmploymentRequester
                    }
                    showPatientTasks={
                        this.props.authentication.areContactsAndTasksEnabled && this.state.hasPatientTaskPermission
                    }
                    workListFilter={this.state.workListFilter}
                />

                {this.state.workListFilter === WorkListFilter.closedRequests &&
                    this.state.ophthalmologyPractitionerRole !== null &&
                    this.state.total > 0 && (
                        <Box mb={8}>
                            <Row columnGap={[0, 4]}>
                                <Col span={[12, 6]}>
                                    <DatePeriodSelection
                                        endDate={
                                            <DateInput
                                                error={
                                                    this.state.exportAfterDate !== null &&
                                                    this.state.exportBeforeDate !== null &&
                                                    !DateTime.from(this.state.exportBeforeDate).isSameOrAfter(
                                                        this.state.exportAfterDate
                                                    )
                                                        ? 'koi_endDate'
                                                        : ''
                                                }
                                                onChange={(value) => this.setState({ exportBeforeDate: value })}
                                                placeholder={translateKey('common.dateFormat')}
                                                value={this.state.exportBeforeDate}
                                            />
                                        }
                                        infixLabel="t/m"
                                        startDate={
                                            <DateInput
                                                onChange={(value) => this.setState({ exportAfterDate: value })}
                                                placeholder={translateKey('common.dateFormat')}
                                                value={this.state.exportAfterDate}
                                            />
                                        }
                                    />
                                </Col>
                                <Col span={[12, 6]}>
                                    <ButtonRow isStackedBelowSmall>
                                        <Button
                                            onClick={() => {
                                                this.downloadRequests();
                                            }}
                                            variant="outline"
                                        >
                                            {translateKey('common.download')}
                                        </Button>
                                    </ButtonRow>
                                </Col>
                            </Row>
                        </Box>
                    )}

                {this.state.workListFilter === WorkListFilter.closedRequests ? (
                    <RequestListAccordion
                        month={this.state.month}
                        searchFilter={this.state.searchFilter}
                        setMonth={async (month) => {
                            await this.setState({ month, page: 1, requests: [] });
                            if (month !== null) {
                                await this.handleSearch(this.state.searchFilter);
                            }
                        }}
                        setMonthlyTotal={async (total) => await this.setState({ monthTotal: total })}
                        setYear={async (year) => await this.setState({ month: null, page: 1, requests: [], year })}
                        year={this.state.year}
                    >
                        <>{this.renderRequestList(this.state.monthTotal)}</>
                    </RequestListAccordion>
                ) : (
                    <>{this.renderRequestList(this.state.total)}</>
                )}

                {this.state.isSearching && <Loader fixedCentered />}

                {this.state.isFirstUseTourVisible && <FirstUseTour url="/requests" />}
            </FullWidthTemplate>
        );
    }

    private renderRequestList(total: number | undefined) {
        if (this.state.isSearching || total === undefined) {
            return null;
        }

        if (this.state.workListFilter === WorkListFilter.patientTasks) {
            return (
                <>
                    <PatientTasksList
                        onSort={async (sortBy) => await this.handleSort(sortBy, true)}
                        patientTasks={this.state.patientTasks}
                        sortableHeaders={['expectedDateCompleted', 'sortDate']}
                        sortBy={this.state.sortByPatientTaskList}
                        sortDirection={this.state.sortDirectionPatientTaskList}
                    />

                    <ListPager
                        isDisabled={this.state.isSearching}
                        itemsPerPage={this.state.itemsPerPage}
                        onChangeItemsPerPage={(itemsPerPage) => {
                            this.handleNavigation(1, itemsPerPage);
                        }}
                        onChangePage={this.handleNavigation}
                        page={this.state.page}
                        total={total}
                    />
                </>
            );
        }

        return (
            <>
                <RequestsList
                    onSort={async (sortBy) => await this.handleSort(sortBy)}
                    requests={this.state.requests}
                    sortableHeaders={['statusLabel', 'sortDate']}
                    sortBy={this.state.sortByRequestList}
                    sortDirection={this.state.sortDirectionRequestList}
                />

                {total > this.state.itemsPerPage && (
                    <ListPager
                        isDisabled={this.state.isSearching}
                        itemsPerPage={this.state.itemsPerPage}
                        onChangeItemsPerPage={(itemsPerPage) => {
                            this.handleNavigation(1, itemsPerPage);
                        }}
                        onChangePage={this.handleNavigation}
                        page={this.state.page}
                        total={total}
                    />
                )}
                {this.renderEmptyWorklistMessages()}
            </>
        );
    }

    private renderEmptyWorklistMessages() {
        if (this.state.requests.length === 0 && this.state.workListFilter) {
            let emptyWorklistMessage;
            if (!isEqual(this.state.searchFilter, emptySearchFilter)) {
                emptyWorklistMessage = 'request.noSearchResultWorklist';
            } else {
                switch (this.state.workListFilter) {
                    case WorkListFilter.myRequests:
                        emptyWorklistMessage = 'request.emptyMyRequestsWorklist';
                        break;
                    case WorkListFilter.colleagueRequests:
                        emptyWorklistMessage = 'request.emptyColleagueRequestsWorklist';
                        break;
                    case WorkListFilter.ongoingRequests:
                        emptyWorklistMessage = 'request.emptyOngoingRequestsWorklist';
                        break;
                    case WorkListFilter.closedRequests:
                        emptyWorklistMessage = 'request.emptyClosedRequestsWorklist';
                        break;
                    default:
                        emptyWorklistMessage = 'request.emptyMyRequestsWorklist';
                        break;
                }
            }

            return (
                <Heading
                    my={8}
                    size="xlarge"
                    textAlign="center"
                >
                    {translateKey(emptyWorklistMessage)}
                </Heading>
            );
        }
    }

    @bind
    private async filterWorkList(workListFilter: WorkListFilter) {
        await this.setState({
            month: workListFilter === WorkListFilter.closedRequests ? DateTime.now().month() : null,
            year: workListFilter === WorkListFilter.closedRequests ? DateTime.now().year() : null,
        });
        this.props.router.navigate('#search', {
            state: {
                itemsPerPage: this.state.itemsPerPage,
                month: this.state.month,
                monthTotal: this.state.monthTotal,
                page: this.state.page,
                searchFilter: this.state.searchFilter,
                workListFilter,
                year: this.state.year,
            },
        });

        const page = this.state.workListFilter !== workListFilter ? 1 : this.state.page;
        await this.setState((state) => ({ ...state, page, workListFilter }));
        await this.search();
    }

    @bind
    private async handleNavigation(page: number, itemsPerPage?: number) {
        this.props.router.navigate('#search', {
            state: {
                itemsPerPage: itemsPerPage ?? this.state.itemsPerPage,
                month: this.state.month,
                monthTotal: this.state.monthTotal,
                page,
                searchFilter: this.state.searchFilter,
                workListFilter: this.state.workListFilter,
                year: this.state.year,
            },
        });
        await this.setState({ page });
        if (itemsPerPage) {
            await this.setState({ itemsPerPage });
        }
        await this.search();
    }

    @bind
    private async handleSearch(searchFilter: ISearchFilter) {
        await this.applyFilter(searchFilter);
        await this.search();
    }

    @bind
    private async applyFilter(searchFilter: ISearchFilter) {
        this.props.router.navigate('#search', {
            state: {
                itemsPerPage: this.state.itemsPerPage,
                month: this.state.month,
                monthTotal: this.state.monthTotal,
                page: 1,
                searchFilter,
                workListFilter: this.state.workListFilter,
                year: this.state.year,
            },
        });
        await this.setState({ page: 1, searchFilter });
    }

    private async search() {
        await this.setState({ isSearching: true });

        const workListFilter = this.state.workListFilter;

        if (workListFilter === WorkListFilter.patientTasks) {
            const textFilter = requestFindFilter(this.state.searchFilter.text, false);
            const result = await fetchJson(
                `/api/patientTasks/getAllForOrganization?page=${this.state.page}&limit=${this.state.itemsPerPage}`,
                'POST',
                {
                    birthDate: textFilter.birthDate,
                    endDate: this.state.searchFilter.endDate,
                    patientTypeOfTasks: this.state.searchFilter.typeOfTasksSelected,
                    searchText: textFilter.text,
                    sortBy: this.state.sortByPatientTaskList,
                    sortDirection: this.state.sortDirectionPatientTaskList,
                    startDate: this.state.searchFilter.startDate,
                    usersAndProfessions: this.state.searchFilter.usersAndProfessions,
                }
            );

            if (!workListFilter || this.state.workListFilter === workListFilter) {
                await this.setState({
                    isSearching: false,
                    patientTaskEpisodeCategories: result.json.patientTaskEpisodeCategories,
                    patientTaskTypes: result.json.patientTaskTypes,
                    patientTasks: result.json.items,
                    total: result.json.total,
                });
            }
        } else {
            const textFilter = requestFindFilter(this.state.searchFilter.text);
            if (textFilter && textFilter.processId) {
                await this.setState(({ searchFilter }) => ({
                    searchFilter: { ...searchFilter, practitionerId: null, serviceId: null },
                    workListFilter: undefined,
                }));
            }

            const url = buildWorklistUrl(
                this.state.searchFilter,
                workListFilter,
                textFilter,
                this.state.page,
                this.state.itemsPerPage,
                { canSeeRequesterAndCareProviderContext: true },
                undefined,
                undefined,
                this.state.year,
                this.state.month,
                this.state.sortByRequestList,
                this.state.sortDirectionRequestList
            );

            const result = await this.abortableFetch.getJson(url);

            // discard the result if the active worklist filter has changed since starting the api call
            if (!workListFilter || this.state.workListFilter === workListFilter) {
                await this.setState({ isSearching: false, requests: result.json.items, total: result.json.total });
            }
        }
    }

    private async loadServiceReaders() {
        const url = `/api/serviceReaders?employmentId=${this.props.authentication.employmentId}&isActive=true`;
        const result = await this.abortableFetch.fetchJson(url, 'GET');
        if (result.json.items.length > 0) {
            await this.setState({ isServiceReader: true });
        }
    }

    private async loadBiometriciansTcoh() {
        const url = `/api/serviceProviders?employmentId=${this.props.authentication.employmentId}&isActive=true&isBiometrist=true&serviceCode=TCOH`;
        const result = await this.abortableFetch.fetchJson(url, 'GET');
        if (result.json.items.length > 0) {
            await this.setState({ isBiometricianTcoh: true });
        }
    }

    private async loadServiceEmploymentRequester() {
        const url = `/api/serviceEmployments?employmentId=${this.props.authentication.employmentId}&isRequester=true`;
        const result = await this.abortableFetch.fetchJson(url, 'GET');
        if (result.json.items.length > 0) {
            await this.setState({ isServiceEmploymentRequester: true });
        }
    }

    private async loadOphthalmologyRole() {
        const result = await this.abortableFetch.fetchJson('/api/serviceProviders/ophthalmologyRole', 'GET');
        await this.setState({ ophthalmologyPractitionerRole: result.json.role });
    }

    private async downloadRequests() {
        window.open(this.getDownloadRequestsUrl());
    }

    private getDownloadRequestsUrl() {
        if (!this.state.ophthalmologyPractitionerRole) {
            return;
        }

        const urlBuilder = new UrlBuilder('/api/requests/exportOphthalmologyClosedTasks');
        const filter = requestFindFilter(this.state.searchFilter.text);

        addSearchFilters(this.state.searchFilter, filter, urlBuilder);

        if (this.state.exportAfterDate) {
            urlBuilder.add('exportAfterDate', this.state.exportAfterDate);
        }

        if (this.state.exportBeforeDate) {
            urlBuilder.add('exportBeforeDate', this.state.exportBeforeDate);
        }

        return urlBuilder.build();
    }

    private async handleSort(sortBy: RequestListHeader | PatientTaskListHeader, isPatientTaskList?: boolean) {
        const statusUpdate = this.getUpdatedSortState(sortBy, isPatientTaskList);
        await this.setState(statusUpdate);
        await this.search();
    }

    private getUpdatedSortState(
        sortBy: RequestListHeader | PatientTaskListHeader,
        isPatientTaskList?: boolean
    ): SortState {
        const sortByKey = isPatientTaskList ? 'sortByPatientTaskList' : 'sortByRequestList';
        const sortDirectionKey = isPatientTaskList ? 'sortDirectionPatientTaskList' : 'sortDirectionRequestList';

        return {
            [sortByKey]: sortBy,
            [sortDirectionKey]:
                this.state[sortDirectionKey] === 'ASC' && this.state[sortByKey] === sortBy
                    ? 'DESC'
                    : ('ASC' as SortDirection),
        };
    }

    private async loadTwoFactorAuthenticationSettings() {
        const url = `/api/otpMethods/${this.props.authentication.practitionerId}/twoFactorAuthenticationSettings`;
        const result = await this.abortableFetch.fetchJson(url, 'GET');
        const isTwoFactorAuthenticationEnabled = result.json.twoFactorAuthenticationType !== null;
        await this.setState({ isTwoFactorAuthenticationEnabled });
    }

    private defaultWorkListFilter() {
        if (
            this.props.authentication.areContactsAndTasksEnabled &&
            (this.props.authentication.profession === Profession.outpatientAssistant ||
                this.props.authentication.profession === Profession.osasNurse)
        ) {
            return WorkListFilter.patientTasks;
        }

        return WorkListFilter.myRequests;
    }
}

function mapStateToProps(state: IReduxState) {
    const { authentication } = state;

    return {
        authentication: {
            areContactsAndTasksEnabled: authentication.areContactsAndTasksEnabled,
            employmentId: authentication.selectedEmploymentId,
            isManager: authentication.isManager,
            organizationId: authentication.organizationId,
            practitionerId: authentication.id,
            profession: authentication.profession,
        },
        isServiceConsumer: serviceConsumersSelector(state).serviceConsumers.length > 0,
    };
}

export const RequestsListContainer = connect(mapStateToProps)(withRouter(RequestsListContainerWrapped));
